import { OccurrenceDeletion } from "@/features/services/components/OccurrenceDeletion";
import type { ComponentProps } from "react";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { getServiceContexts } from "@/api/authorization";
import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck2,
  CalendarDays,
  Church,
  Clock3,
  MapPin,
  NotebookText,
  Pencil,
  Users,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

import {
  getServiceOccurrence,
  updateServiceOccurrence,
} from "@/api/services";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { EditServiceDialog } from "@/features/services/components/EditServiceDialog";
import { ServiceAttendeeRoster } from "@/features/services/components/ServiceAttendeeRoster";
import type {
  ServiceOccurrence,
  ServiceOccurrenceUpdatePayload,
} from "@/types/service";
import { getServiceVisitors } from "@/api/visitors";

export function ServiceProfilePage() {
  const { churchId } = useAuth();
  const { serviceId } = useParams();
  const queryClient = useQueryClient();
  const contextsQuery = useQuery({ queryKey: ["service-contexts", churchId], queryFn: () => getServiceContexts(churchId!), enabled: !!churchId });

  const [editDialogOpen, setEditDialogOpen] =
    useState(false);

  const [editError, setEditError] = useState<
    string | null
  >(null);

  const servicesQuery = useQuery({
    queryKey: ["service-occurrence", churchId, serviceId],
    enabled: !!churchId && !!serviceId,
    queryFn: () => getServiceOccurrence(churchId!, serviceId!),
  });

  const { permissions } = useEffectivePermissions(servicesQuery.data?.ministry_id, !!servicesQuery.data);
  const canViewAttendance = permissions.has("ATTENDANCE_VIEW");
  const canViewVisitors = canViewAttendance && permissions.has("VISITORS_VIEW");
  const visitorsQuery = useQuery({
    queryKey: [
      "service-visitors",
      churchId,
      serviceId,
    ],
    enabled: !!churchId && !!serviceId && canViewVisitors,
    queryFn: () =>
      getServiceVisitors(
        churchId!,
        serviceId!
      ),
  });

  const updateMutation = useMutation({
    mutationFn: (
      payload: ServiceOccurrenceUpdatePayload
    ) => {
      if (!churchId || !serviceId) {
        throw new Error(
          "Church or service occurrence is unavailable."
        );
      }

      return updateServiceOccurrence(
        churchId,
        serviceId,
        payload
      );
    },

    onSuccess: async () => {
      setEditError(null);
      setEditDialogOpen(false);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["service-occurrence", churchId, serviceId] }),
        queryClient.invalidateQueries({ queryKey: ["occurrence-attendance", churchId, serviceId] }),
        queryClient.invalidateQueries({
          queryKey: [
            "service-occurrences",
            churchId,
          ],
        }),

        queryClient.invalidateQueries({
          queryKey: ["services", churchId],
        }),

        queryClient.invalidateQueries({
          queryKey: [
            "attendance-roster",
            churchId,
            serviceId,
          ],
        }),

        queryClient.invalidateQueries({
          queryKey: [
            "attendance-records",
            churchId,
          ],
        }),

        queryClient.invalidateQueries({
          queryKey: ["dashboard", churchId],
        }),
      ]);
    },

    onError: (error) => {
      setEditError(
        getErrorMessage(
          error,
          "Unable to update service."
        )
      );
    },
  });

  if (
    servicesQuery.isLoading
  ) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (
    servicesQuery.isError ||
    !servicesQuery.data
  ) {
    return (
      <PageContainer>
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-6 text-rose-300">
          Unable to load service.
        </div>
      </PageContainer>
    );
  }

  const service = servicesQuery.data;

  if (!service) {
    return (
      <PageContainer>
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6 text-amber-300">
          Service occurrence not found.
        </div>
      </PageContainer>
    );
  }

  const visitorCount =
  canViewVisitors ? visitorsQuery.data?.visitor_count : undefined;

  return (
    <>
      <p className="mb-3 text-sm text-indigo-300">{contextsQuery.data?.find(c => c.ministry_id === service.ministry_id)?.name ?? (service.ministry_id ? "Ministry service" : "Main Church")} · Attendance may be recorded at any time for eligible attendees.</p>
      <ServiceProfileContent
        service={service}
        churchId={churchId!}
        visitorCount={visitorCount}
        canViewAttendance={canViewAttendance}
        onEdit={permissions.has("SERVICES_MANAGE") ? () => {
          setEditError(null);
          setEditDialogOpen(true);
        } : undefined}
      />

      <OccurrenceDeletion churchId={churchId!} service={service} />

      <EditServiceDialog
        open={editDialogOpen}
        service={service}
        isSubmitting={updateMutation.isPending}
        errorMessage={editError}
        onClose={() => {
          if (!updateMutation.isPending) {
            setEditDialogOpen(false);
            setEditError(null);
          }
        }}
        onSubmit={async (payload) => {
          await updateMutation.mutateAsync(payload);
        }}
      />
    </>
  );
}

function ServiceProfileContent({
  service,
  churchId,
  visitorCount,
  canViewAttendance,
  onEdit,
}: {
  service: ServiceOccurrence;
  churchId: string;
  visitorCount?: number;
  canViewAttendance: boolean;
  onEdit?: () => void;
}) {
  const canCheckIn =
    canViewAttendance && service.status !== "CANCELLED";

  return (
    <PageContainer>
      <div className="mb-5 flex items-center gap-2 text-sm text-slate-400">
        <ServiceLink ministryId={service.ministry_id}
          to={service.ministry_id ? `/services?ministry_id=${service.ministry_id}` : "/services"}
          className="flex items-center gap-2 hover:text-white"
        >
          <ArrowLeft size={15} />
          Services
        </ServiceLink>

        <span>/</span>

        <span className="text-white">
          {service.title}
        </span>
      </div>

      <ServiceHero
        service={service}
        canCheckIn={canCheckIn}
        onEdit={onEdit}
      />

      <div className="mt-7">
        {canViewAttendance && <ServiceAttendeeRoster key={service.id} churchId={churchId} service={service} visitorCount={visitorCount}/>}
      </div>
      <div className="mt-7 grid gap-6 xl:grid-cols-2">
        <ServiceInformationCard service={service}/>
        <ServiceActionsCard service={service} canCheckIn={canCheckIn} onEdit={onEdit}/>
      </div>
    </PageContainer>
  );
}

function ServiceHero({
  service,
  canCheckIn,
  onEdit,
}: {
  service: ServiceOccurrence;
  canCheckIn: boolean;
  onEdit?: () => void;
}) {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-indigo-950/30 px-7 py-8 shadow-xl shadow-black/20">
      <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="absolute -bottom-28 left-24 h-56 w-56 rounded-full bg-violet-500/5 blur-3xl" />

      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-indigo-500/15 text-indigo-400">
            <Church size={30} />
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                {service.title}
              </h1>

              <ServiceStatus
                status={service.status}
              />
            </div>

            <p className="mt-3 text-base text-slate-300">
              {formatServiceType(
                service.service_type
              )}
            </p>

            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-400">
              <HeroDetail
                icon={<CalendarDays size={17} />}
                label={formatDate(
                  service.starts_at
                )}
              />

              <HeroDetail
                icon={<Clock3 size={17} />}
                label={formatTimeRange(
                  service.starts_at,
                  service.ends_at
                )}
              />

              <HeroDetail
                icon={<MapPin size={17} />}
                label={
                  service.location ||
                  "No location recorded"
                }
              />
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 sm:flex-row lg:flex-col">
          {canCheckIn ? (
            <ServiceLink ministryId={service.ministry_id}
              to={`/attendance/${service.id}`}
              className="group flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3 font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-950/30 active:translate-y-0"
            >
              <CalendarCheck2 size={18} />

              Open Check-In

              <ArrowRight
                size={16}
                className="transition group-hover:translate-x-1"
              />
            </ServiceLink>
          ) : (
            <ServiceLink ministryId={service.ministry_id}
              to={`/attendance/${service.id}`}
              className="group flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-950/50 px-5 py-3 font-semibold text-white transition hover:border-indigo-500/40 hover:bg-slate-800"
            >
              View Attendance

              <ArrowRight
                size={16}
                className="transition group-hover:translate-x-1"
              />
            </ServiceLink>
          )}

          <Button
            variant="secondary"
            disabled={!onEdit}
            onClick={onEdit}
          >
            <span className="flex items-center justify-center gap-2">
              <Pencil size={17} />
              Edit / Reschedule
            </span>
          </Button>

          <ServiceLink ministryId={service.ministry_id}
            to={service.ministry_id ? `/services?ministry_id=${service.ministry_id}` : "/attendance"}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-950/40 px-5 py-3 text-sm font-semibold text-slate-300 transition hover:border-slate-600 hover:bg-slate-800 hover:text-white"
          >
            {service.ministry_id ? "Ministry Services" : "Attendance Overview"}
          </ServiceLink>
        </div>
      </div>
    </section>
  );
}

function ServiceInformationCard({
  service,
}: {
  service: ServiceOccurrence;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-800 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-indigo-500/15 p-3 text-indigo-400">
            <NotebookText size={21} />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white">
              Service Information
            </h2>

            <p className="text-sm text-slate-500">
              Schedule and occurrence details
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-6">
        <InformationRow
          icon={<Church size={18} />}
          label="Service Type"
          value={formatServiceType(
            service.service_type
          )}
        />

        <InformationRow
          icon={<CalendarDays size={18} />}
          label="Date"
          value={formatDate(service.starts_at)}
        />

        <InformationRow
          icon={<Clock3 size={18} />}
          label="Time"
          value={formatTimeRange(
            service.starts_at,
            service.ends_at
          )}
        />

        <InformationRow
          icon={<MapPin size={18} />}
          label="Location"
          value={
            service.location || "Not recorded"
          }
        />

        <InformationRow
          icon={<Users size={18} />}
          label="Capacity"
          value={
            service.capacity
              ? `${service.capacity} people`
              : "No capacity set"
          }
        />

        <InformationRow
          icon={<NotebookText size={18} />}
          label="Notes"
          value={
            service.notes || "No notes recorded"
          }
        />
      </div>
    </Card>
  );
}

function ServiceActionsCard({
  service,
  canCheckIn,
  onEdit,
}: {
  service: ServiceOccurrence;
  canCheckIn: boolean;
  onEdit?: () => void;
}) {
  return (
    <Card className="relative overflow-hidden p-6">
      <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-indigo-500/10 blur-3xl" />

      <div className="relative">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-400">
          <CalendarCheck2 size={22} />
        </div>

        <h2 className="mt-5 text-xl font-semibold text-white">
          Service Actions
        </h2>

        <p className="mt-2 text-sm leading-6 text-slate-400">
          Manage attendance and review this service occurrence.
        </p>

        <div className="mt-6 space-y-3">
          <ServiceLink ministryId={service.ministry_id}
            to={`/attendance/${service.id}`}
            className={`group flex items-center justify-between rounded-xl px-5 py-4 font-semibold transition ${
              canCheckIn
                ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-950/30"
                : "border border-slate-700 bg-slate-950/50 text-white hover:border-indigo-500/40 hover:bg-slate-800"
            }`}
          >
            <span>
              {canCheckIn
                ? "Open Check-In Roster"
                : "View Attendance Roster"}
            </span>

            <ArrowRight
              size={17}
              className="transition group-hover:translate-x-1"
            />
          </ServiceLink>

          <button
            type="button"
            disabled={!onEdit}
            onClick={onEdit}
            className="group flex w-full items-center justify-between rounded-xl border border-slate-700 bg-slate-950/40 px-5 py-4 font-medium text-slate-300 transition hover:border-indigo-500/40 hover:bg-slate-800 hover:text-white"
          >
            <span className="flex items-center gap-2">
              <Pencil size={16} />
              Edit / Reschedule
            </span>

            <ArrowRight
              size={17}
              className="transition group-hover:translate-x-1"
            />
          </button>

          <ServiceLink ministryId={service.ministry_id}
            to={service.ministry_id ? `/services?ministry_id=${service.ministry_id}` : "/attendance"}
            className="group flex items-center justify-between rounded-xl border border-slate-700 bg-slate-950/40 px-5 py-4 font-medium text-slate-300 transition hover:border-slate-600 hover:bg-slate-800 hover:text-white"
          >
            <span>Attendance Overview</span>

            <ArrowRight
              size={17}
              className="transition group-hover:translate-x-1"
            />
          </ServiceLink>

          <ServiceLink ministryId={service.ministry_id}
            to="/services"
            className="group flex items-center justify-between rounded-xl border border-slate-700 bg-slate-950/40 px-5 py-4 font-medium text-slate-300 transition hover:border-slate-600 hover:bg-slate-800 hover:text-white"
          >
            <span>All Services</span>

            <ArrowRight
              size={17}
              className="transition group-hover:translate-x-1"
            />
          </ServiceLink>
        </div>
      </div>
    </Card>
  );
}

function InformationRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 text-slate-500">
          {icon}
        </span>

        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {label}
          </p>

          <p className="mt-2 break-words font-medium leading-6 text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function HeroDetail({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500">
        {icon}
      </span>

      <span>{label}</span>
    </div>
  );
}

function ServiceStatus({
  status,
}: {
  status: string;
}) {
  const normalized = status.toUpperCase();

  const className =
    normalized === "COMPLETED"
      ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300"
      : normalized === "CANCELLED"
        ? "border-rose-500/20 bg-rose-500/10 text-rose-300"
        : "border-indigo-500/20 bg-indigo-500/10 text-indigo-300";

  return (
    <span
      className={`rounded-full border px-3 py-1 text-xs font-semibold ${className}`}
    >
      {formatServiceType(status)}
    </span>
  );
}

function formatServiceType(value: string) {
  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTimeRange(
  startsAt: string,
  endsAt?: string | null
) {
  const start = new Date(startsAt);

  if (Number.isNaN(start.getTime())) {
    return startsAt;
  }

  const startTime = start.toLocaleTimeString(
    undefined,
    {
      hour: "numeric",
      minute: "2-digit",
    }
  );

  if (!endsAt) {
    return startTime;
  }

  const end = new Date(endsAt);

  if (Number.isNaN(end.getTime())) {
    return startTime;
  }

  return `${startTime} - ${end.toLocaleTimeString(
    undefined,
    {
      hour: "numeric",
      minute: "2-digit",
    }
  )}`;
}

function getErrorMessage(
  error: unknown,
  fallback: string
) {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (
      typeof response?.data?.detail === "string"
    ) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}


function ServiceLink({ ministryId, ...props }: ComponentProps<typeof Link> & { ministryId?: string | null }) {
  const { permissions } = useEffectivePermissions(ministryId);
  if (typeof props.to === "string" && props.to.startsWith("/attendance/") && !permissions.has("ATTENDANCE_VIEW")) return null;
  return <Link {...props} />;
}
